import classNames from 'classnames'

import { getTestId } from '@marketplace-web/vendor-abstractions/web-ui-util'

type Props = {
  totalImages: number
  currentIndex: number
  testId?: string
}

const dotSize = 6
const gap = 4

const getVisibleDotIndices = (current: number, total: number): Array<number> => {
  if (total <= 5) {
    return Array.from({ length: total }, (_, index) => index)
  }

  let start = Math.max(0, current - 2)
  let end = Math.min(total - 1, current + 2)

  if (end - start < 4) {
    if (start === 0) {
      end = Math.min(4, total - 1)
    } else {
      start = Math.max(0, total - 5)
    }
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

const shouldDotBeSmall = (
  dotIndex: number,
  visibleIndices: Array<number>,
  current: number,
): boolean => {
  if (visibleIndices.length < 5) return false

  const positionInWindow = visibleIndices.indexOf(dotIndex)
  const isEdgeDot = positionInWindow === 0 || positionInWindow === visibleIndices.length - 1

  if (!isEdgeDot) return false

  const isActive = dotIndex === current
  const neighborIndex =
    positionInWindow === 0 ? visibleIndices[1] : visibleIndices[visibleIndices.length - 2]
  const neighborIsActive = neighborIndex === current

  return !isActive && !neighborIsActive
}

const ImageCarouselIndicators = ({ totalImages, currentIndex, testId }: Props) => {
  if (totalImages <= 1) return null

  if (totalImages <= 5) {
    return (
      <>
        {Array.from({ length: totalImages }, (_, imageIndex) => (
          <span
            key={imageIndex}
            data-testid={getTestId(testId, `carousel-dot-${imageIndex}`)}
            className={classNames('new-item-box__carousel-dot', {
              'new-item-box__carousel-dot--active': imageIndex === currentIndex,
            })}
          />
        ))}
      </>
    )
  }

  const windowStart = Math.max(0, Math.min(currentIndex - 2, totalImages - 5))
  const offset = windowStart * -(dotSize + gap)
  const visibleIndices = getVisibleDotIndices(currentIndex, totalImages)

  return (
    <div
      className="new-item-box__carousel-dots-slider"
      style={{ transform: `translateX(${offset}px)` }}
    >
      {Array.from({ length: totalImages }, (_, imageIndex) => {
        const isSmall = shouldDotBeSmall(imageIndex, visibleIndices, currentIndex)

        return (
          <span
            key={imageIndex}
            data-testid={getTestId(testId, `carousel-dot-${imageIndex}`)}
            className={classNames('new-item-box__carousel-dot', {
              'new-item-box__carousel-dot--active': imageIndex === currentIndex,
              'new-item-box__carousel-dot--small': isSmall,
            })}
          />
        )
      })}
    </div>
  )
}

export default ImageCarouselIndicators
